/**
 * Estimating Book (S6 + S7) dashboard.
 *
 * Left: list of every sheet/table across both workbooks (consolidated view).
 * Right: the selected table — base rate matrix plus its factor table.
 * Factors are editable for authorized users: ranges show min/max with the
 * midpoint as the calculation base, all writes audit-logged. Factors with a
 * schedule/material meaning also produce derived rows in the Comparison Hub.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  styles, ACCENT, Spinner, ErrorBox, fmtHours, fmtInt, apiGet, apiSend, errMsg,
} from './rbShared';

export default function EstimatingBookDashboard({ canEdit }) {
  const { instance, accounts } = useMsal();
  const [tables, setTables] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setError(null);
    apiGet(instance, accounts, '/rate-benchmarks/eb/tables')
      .then((r) => {
        setTables(r.data || []);
        setSelectedId((cur) => cur || (r.data && r.data.length ? r.data[0].id : null));
      })
      .catch((e) => setError(errMsg(e)));
  }, [instance, accounts]);

  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorBox error={error} onRetry={load} />;
  if (!tables) return <Spinner label="Loading Estimating Book…" />;

  const grouped = { S6: tables.filter((t) => t.workbook === 'S6'), S7: tables.filter((t) => t.workbook === 'S7') };

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ width: 250, flexShrink: 0, maxHeight: '72vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
        {['S6', 'S7'].map((wb) => (
          <div key={wb}>
            <div style={{ padding: '7px 10px', fontWeight: 700, fontSize: '0.75rem', color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0 }}>
              {wb === 'S6' ? 'Section 6 — Shop' : 'Section 7 — Field'} ({grouped[wb].length} tables)
            </div>
            {grouped[wb].map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px',
                  border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                  background: selectedId === t.id ? '#f0fdfa' : '#fff',
                  borderLeft: selectedId === t.id ? `3px solid ${ACCENT}` : '3px solid transparent',
                  fontSize: '0.78rem',
                }}
              >
                <div style={{ fontWeight: 600, color: '#111827' }}>{t.section_code || t.sheet_name}</div>
                <div style={{ color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t.title || ''}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                  {fmtInt(t.parts)} rows · {fmtInt(t.active_factors)} factors
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {selectedId
          ? <EbTableDetail tableId={selectedId} canEdit={canEdit} onFactorsChanged={load} />
          : <div style={styles.card}>Select a table.</div>}
      </div>
    </div>
  );
}

function EbTableDetail({ tableId, canEdit, onFactorsChanged }) {
  const { instance, accounts } = useMsal();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [editingFactor, setEditingFactor] = useState(null); // factor object or 'new'
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    setDetail(null);
    apiGet(instance, accounts, `/rate-benchmarks/eb/tables/${tableId}`)
      .then(setDetail)
      .catch((e) => setError(errMsg(e)));
  }, [instance, accounts, tableId]);

  useEffect(() => { load(); }, [load]);

  const matrix = useMemo(() => {
    if (!detail) return null;
    const cols = [];
    const seen = new Set();
    (detail.entries || []).forEach((e) => {
      if (!seen.has(e.size_raw)) { seen.add(e.size_raw); cols.push(e); }
    });
    const byPart = new Map();
    (detail.entries || []).forEach((e) => {
      if (!byPart.has(e.part_id)) byPart.set(e.part_id, {});
      byPart.get(e.part_id)[e.size_raw] = e.hours;
    });
    return { cols, byPart };
  }, [detail]);

  const saveFactor = async (form) => {
    setBusy(true);
    try {
      if (form.id) {
        await apiSend(instance, accounts, 'PUT', `/rate-benchmarks/eb/factors/${form.id}`, form);
      } else {
        await apiSend(instance, accounts, 'POST', '/rate-benchmarks/eb/factors', { ...form, eb_table_id: tableId });
      }
      setEditingFactor(null);
      load();
      onFactorsChanged();
    } catch (e) {
      alert(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteFactor = async (f) => {
    if (!window.confirm(`Delete factor "${f.label}"? This is audit-logged.`)) return;
    setBusy(true);
    try {
      await apiSend(instance, accounts, 'DELETE', `/rate-benchmarks/eb/factors/${f.id}`);
      load();
      onFactorsChanged();
    } catch (e) {
      alert(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorBox error={error} onRetry={load} />;
  if (!detail) return <Spinner label="Loading table…" />;

  const t = detail.table || {};
  return (
    <div>
      <div style={{ ...styles.card, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{t.section_code} — {t.title}</div>
        {t.subtitle && <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>{t.subtitle}</div>}
        {t.notes && <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: 4, whiteSpace: 'pre-wrap' }}>{t.notes}</div>}
      </div>

      {/* Factor table */}
      <div style={{ ...styles.card, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontWeight: 700 }}>Factors</div>
          <span style={{ marginLeft: 8, fontSize: '0.72rem', color: '#6b7280' }}>
            ranged factors use the <strong>average</strong> as calculation base · edits audit-logged
          </span>
          {canEdit && (
            <button style={{ ...styles.btn, marginLeft: 'auto', padding: '4px 10px' }} onClick={() => setEditingFactor('new')}>+ Add factor</button>
          )}
        </div>
        {(detail.factors || []).length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
            No factors on this sheet — base table applies at factor 1.0.
            {canEdit ? ' Use "+ Add factor" to define one.' : ''}
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Factor</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Range</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Value (calc base)</th>
                <th style={styles.th}>Formula</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Active</th>
                {canEdit && <th style={styles.th} />}
              </tr>
            </thead>
            <tbody>
              {(detail.factors || []).map((f) => (
                <tr key={f.id} style={{ opacity: f.is_active ? 1 : 0.45 }}>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{f.label}{f.note ? <span style={{ color: '#9ca3af', fontWeight: 400 }}> — {f.note}</span> : null}</td>
                  <td style={styles.tdNum}>
                    {f.factor_min !== null && f.factor_max !== null ? `${f.factor_min} – ${f.factor_max}` : ''}
                  </td>
                  <td style={{ ...styles.tdNum, fontWeight: 700, color: ACCENT }}>{f.factor_value !== null ? fmtHours(f.factor_value) : ''}</td>
                  <td style={styles.td}>{f.formula_text || ''}</td>
                  <td style={styles.td}>
                    <span style={styles.badge(f.source === 'manual' ? '#ede9fe' : '#f3f4f6', f.source === 'manual' ? '#5b21b6' : '#6b7280')}>{f.source}</span>
                    {f.updated_by ? <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#9ca3af' }}>{f.updated_by}</span> : null}
                  </td>
                  <td style={styles.td}>{f.is_active ? '✓' : '—'}</td>
                  {canEdit && (
                    <td style={styles.td}>
                      <button style={{ ...styles.btnGhost, padding: '2px 8px' }} disabled={busy} onClick={() => setEditingFactor(f)}>Edit</button>{' '}
                      <button style={{ ...styles.btnGhost, padding: '2px 8px', color: '#b91c1c' }} disabled={busy} onClick={() => deleteFactor(f)}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Base rate matrix */}
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Base table ({fmtInt((detail.parts || []).length)} rows)</div>
      <div style={styles.scroll}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, position: 'sticky', left: 0, zIndex: 3 }}>Description</th>
              <th style={styles.th}>Material</th>
              <th style={styles.th}>Sch</th>
              <th style={styles.th}>Rating</th>
              {(matrix?.cols || []).map((c) => (
                <th key={c.size_raw} style={{ ...styles.th, textAlign: 'right' }}>{c.size_raw}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(detail.parts || []).map((p) => (
              <tr key={p.id}>
                <td style={{ ...styles.td, position: 'sticky', left: 0, background: '#fff', fontWeight: 600, zIndex: 1 }} title={p.description_raw}>
                  {p.description_norm || p.description_raw}
                </td>
                <td style={styles.td}>{p.material_norm || ''}</td>
                <td style={styles.td}>{p.sch_norm || ''}</td>
                <td style={styles.td}>{p.rating || ''}</td>
                {(matrix?.cols || []).map((c) => {
                  const v = matrix.byPart.get(p.id)?.[c.size_raw];
                  return <td key={c.size_raw} style={styles.tdNum}>{v !== undefined ? fmtHours(v) : ''}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingFactor && (
        <FactorEditModal
          factor={editingFactor === 'new' ? null : editingFactor}
          busy={busy}
          onSave={saveFactor}
          onClose={() => setEditingFactor(null)}
        />
      )}
    </div>
  );
}

function FactorEditModal({ factor, busy, onSave, onClose }) {
  const [form, setForm] = useState(() => ({
    id: factor?.id,
    label: factor?.label || '',
    factor_min: factor?.factor_min ?? '',
    factor_max: factor?.factor_max ?? '',
    factor_value: factor?.factor_value ?? '',
    formula_text: factor?.formula_text || '',
    note: factor?.note || '',
    is_active: factor ? !!factor.is_active : true,
  }));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const midpoint = form.factor_min !== '' && form.factor_max !== ''
    ? (parseFloat(form.factor_min) + parseFloat(form.factor_max)) / 2 : null;

  const field = (label, key, props = {}) => (
    <label style={{ display: 'block', marginBottom: 10, fontSize: '0.8rem' }}>
      <span style={{ display: 'block', fontWeight: 600, marginBottom: 3 }}>{label}</span>
      <input
        style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        {...props}
      />
    </label>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 460, maxWidth: '95vw', padding: '1.1rem 1.3rem', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '1rem' }}>{factor ? 'Edit factor' : 'Add factor'}</h3>
        {field('Label', 'label', { placeholder: 'e.g. Sch. 80 – 10" to 24"' })}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>{field('Range min', 'factor_min', { type: 'number', step: '0.01' })}</div>
          <div style={{ flex: 1 }}>{field('Range max', 'factor_max', { type: 'number', step: '0.01' })}</div>
          <div style={{ flex: 1 }}>{field('Value', 'factor_value', { type: 'number', step: '0.01', placeholder: midpoint !== null && Number.isFinite(midpoint) ? `avg ${midpoint.toFixed(3)}` : '' })}</div>
        </div>
        {midpoint !== null && Number.isFinite(midpoint) && form.factor_value === '' && (
          <div style={{ fontSize: '0.72rem', color: ACCENT, marginTop: -6, marginBottom: 8 }}>
            Range given — the average {midpoint.toFixed(3)} will be used as the calculation base.
          </div>
        )}
        {field('Formula (optional)', 'formula_text', { placeholder: 'e.g. 1.4*SS' })}
        {field('Note (optional)', 'note')}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', marginBottom: 14 }}>
          <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
          Active (inactive factors are kept but ignored in comparisons)
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={styles.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          <button
            style={styles.btn}
            disabled={busy || !form.label.trim()}
            onClick={() => onSave(form)}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
